/**
 * Cart shipping widget: asks for a CEP and checks it against two free-shipping
 * tiers — a local delivery city (e.g. Fortaleza/CE) with a lower threshold,
 * and a broader list of eligible states with a higher threshold — otherwise
 * shows the cheapest real shipping rate for that CEP. Persists the CEP in
 * localStorage.
 */
class CartShippingWidget extends HTMLElement {
  static storageZipKey = 'wintors:shipping_cep';
  static storageStateKey = 'wintors:shipping_state';
  static storageCityKey = 'wintors:shipping_city';

  connectedCallback() {
    this.panels = {
      ask: this.querySelector('[data-shipping-state="ask"]'),
      loading: this.querySelector('[data-shipping-state="loading"]'),
      eligible: this.querySelector('[data-shipping-state="eligible"]'),
      'not-eligible': this.querySelector('[data-shipping-state="not-eligible"]'),
    };
    this.input = this.querySelector('[data-shipping-cep-input]');
    this.errorEl = this.querySelector('[data-shipping-error]');
    this.rateEl = this.querySelector('[data-shipping-rate]');
    this.eligibleMessageEl = this.querySelector('[data-shipping-eligible-message]');
    this.progressEl = this.querySelector('[data-shipping-progress]');
    this.progressFillEl = this.querySelector('[data-shipping-progress-fill]');
    this.eligibleStates = (this.dataset.eligibleStates || '')
      .split(',')
      .map((state) => state.trim().toUpperCase())
      .filter(Boolean);
    this.localCity = (this.dataset.localCity || '').trim().toLowerCase();
    this.localState = (this.dataset.localState || '').trim().toUpperCase();

    this.querySelectorAll('[data-shipping-submit]').forEach((button) =>
      button.addEventListener('click', () => this.handleSubmit())
    );
    this.querySelectorAll('[data-shipping-change]').forEach((button) =>
      button.addEventListener('click', () => this.reset())
    );

    if (this.input) {
      this.input.addEventListener('input', (event) => this.#maskCep(event));
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.handleSubmit();
        }
      });
    }

    this.#restore();

    // The theme re-renders this section's markup on every cart change
    // (add/remove/quantity), which resets our runtime panel visibility.
    // Restore the resolved state on the next frame, after that re-render.
    document.addEventListener('cart:update', this.#handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#handleCartUpdate);
  }

  #handleCartUpdate = () => {
    requestAnimationFrame(() => this.#restore());
  };

  #maskCep(event) {
    const digits = event.target.value.replace(/\D/g, '').slice(0, 8);
    event.target.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  #restore() {
    const zip = localStorage.getItem(CartShippingWidget.storageZipKey);
    const state = localStorage.getItem(CartShippingWidget.storageStateKey);
    const city = localStorage.getItem(CartShippingWidget.storageCityKey) || '';

    if (zip && state) {
      if (this.input) this.input.value = zip.length > 5 ? `${zip.slice(0, 5)}-${zip.slice(5)}` : zip;
      this.#resolve(state, zip, city);
    } else {
      this.#show('ask');
    }
  }

  reset() {
    localStorage.removeItem(CartShippingWidget.storageZipKey);
    localStorage.removeItem(CartShippingWidget.storageStateKey);
    localStorage.removeItem(CartShippingWidget.storageCityKey);
    if (this.input) this.input.value = '';
    this.#hideError();
    this.#show('ask');
  }

  async handleSubmit() {
    this.#hideError();
    const cep = (this.input?.value || '').replace(/\D/g, '');

    if (!/^\d{8}$/.test(cep)) {
      this.#showError('Digite um CEP válido.');
      return;
    }

    this.#show('loading');

    try {
      const response = await fetch(`https://brasilapi.com.br/api/cep/v1/${cep}`);
      if (!response.ok) throw new Error('cep-not-found');
      const data = await response.json();

      localStorage.setItem(CartShippingWidget.storageZipKey, cep);
      localStorage.setItem(CartShippingWidget.storageStateKey, data.state);
      localStorage.setItem(CartShippingWidget.storageCityKey, data.city || '');

      await this.#resolve(data.state, cep, data.city);
    } catch (error) {
      this.#show('ask');
      this.#showError('Não conseguimos localizar esse CEP. Confira e tente novamente.');
    }
  }

  /**
   * @param {string} state - Two-letter Brazilian state code (UF).
   * @param {string} cep - Eight-digit CEP, digits only.
   * @param {string} [city] - City name, as returned by the CEP lookup.
   */
  async #resolve(state, cep, city) {
    const normalizedState = String(state).toUpperCase();
    const normalizedCity = String(city || '').trim().toLowerCase();

    const isLocal = Boolean(this.localCity) && normalizedState === this.localState && normalizedCity === this.localCity;
    const isRegional = this.eligibleStates.includes(normalizedState);

    if (isLocal || isRegional) {
      const thresholdCents = isLocal ? Number(this.dataset.localThresholdCents) : Number(this.dataset.generalThresholdCents);
      this.#showEligible(thresholdCents);
      return;
    }

    this.#show('loading');

    try {
      const url = `/cart/shipping_rates.json?shipping_address[zip]=${cep}&shipping_address[country]=BR&shipping_address[province]=${state}`;
      const response = await fetch(url);
      const data = await response.json();
      const rates = Array.isArray(data.shipping_rates) ? data.shipping_rates : [];

      if (rates.length === 0) throw new Error('no-rates');

      const cheapest = rates.reduce(
        (min, rate) => (Number(rate.price) < Number(min.price) ? rate : min),
        rates[0]
      );

      if (this.rateEl) {
        this.rateEl.textContent = Number(cheapest.price) === 0 ? 'Grátis' : this.#formatCurrency(cheapest.price);
      }
    } catch (error) {
      if (this.rateEl) this.rateEl.textContent = 'calculado no checkout';
    }

    this.#show('not-eligible');
  }

  /**
   * Fills in the eligible panel's progress message/bar for the given
   * threshold and shows it. The threshold depends on whether the resolved
   * CEP matched the local delivery city or the broader eligible-states list.
   * @param {number} thresholdCents
   */
  #showEligible(thresholdCents) {
    const cartTotalCents = Number(this.dataset.cartTotalCents) || 0;
    const remainingCents = thresholdCents - cartTotalCents;
    const unlocked = remainingCents <= 0;
    let progress = thresholdCents > 0 ? (cartTotalCents / thresholdCents) * 100 : 0;
    progress = Math.min(100, Math.max(0, progress));

    if (this.eligibleMessageEl) {
      this.eligibleMessageEl.classList.toggle('shipping-widget__message--unlocked', unlocked);
      this.eligibleMessageEl.textContent = unlocked
        ? 'Você Ganhou Frete Grátis 🎉'
        : `Faltam ${this.#formatCurrency(remainingCents / 100)} para o Frete Grátis`;
    }

    if (this.progressEl) this.progressEl.setAttribute('aria-valuenow', String(Math.round(progress)));
    if (this.progressFillEl) this.progressFillEl.style.width = `${progress}%`;

    this.#show('eligible');
  }

  /** @param {string | number} value */
  #formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value));
  }

  /** @param {'ask' | 'loading' | 'eligible' | 'not-eligible'} state */
  #show(state) {
    Object.entries(this.panels).forEach(([key, panel]) => {
      if (panel) panel.hidden = key !== state;
    });
  }

  /** @param {string} message */
  #showError(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
  }

  #hideError() {
    if (this.errorEl) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }
}

if (!customElements.get('cart-shipping-widget')) {
  customElements.define('cart-shipping-widget', CartShippingWidget);
}
